// TestimonialsSection — customer reviews for the web landing.
//
// Sits directly below <GuaranteeSection /> in LandingV2 so the
// user-trust story flows: refund promise → real testimonials → price.
// Language + RTL are passed in from the parent so this file has no
// dependency on the LandingV2 context primitives (keeps it reusable).

import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { TESTIMONIALS } from "@/lib/testimonialsData";

const COPY = {
  ar: {
    // Simplified per owner (Sep 30, 2026): drop the H2 headline +
    // subtitle. The eyebrow becomes the sole title, promoted to a
    // large, bold display style.
    title: "آراء المستخدمين",
  },
  en: {
    title: "User Reviews",
  },
};

const ACCENT_BG = {
  brand:   "bg-brand-500 text-white",
  emerald: "bg-emerald-600 text-white",
  violet:  "bg-violet-600 text-white",
  sky:     "bg-sky-600 text-white",
  amber:   "bg-amber-500 text-white",
};

export default function TestimonialsSection({ lang = "ar" }) {
  const isRTL = lang === "ar";
  const t = COPY[lang] || COPY.ar;

  return (
    <section
      className="relative py-14 md:py-20"
      data-testid="lv2-testimonials"
      style={{
        background:
          "linear-gradient(180deg, #FFF7EE 0%, #FFEDD5 50%, #FFFFFF 100%)",
      }}
    >
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-10 md:mb-14" dir={isRTL ? "rtl" : "ltr"}>
          {/* Owner simplified this to a single big title (Sep 30, 2026).
              No eyebrow badge, no subtitle — the heading is the entire
              header. Sized larger than a normal H2 so it commands the
              section on its own. */}
          <h2
            className="font-black text-neutral-900 leading-[1.05] tracking-tight text-5xl sm:text-6xl md:text-7xl"
            data-testid="lv2-testimonials-title"
          >
            {t.title}
          </h2>
        </div>

        {/* 4 cards balance best as 2×2 on tablets and 4-in-a-row on
            desktop; single column on mobile. Cards are equal-height so
            no card feels orphaned. */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 items-stretch">
          {TESTIMONIALS.map((r, i) => (
            <TestimonialCard key={r.id} r={r} idx={i} lang={lang} isRTL={isRTL} />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes tsTwinkle {
          0%, 100% { transform: scale(1);    filter: drop-shadow(0 0 0 rgba(245,166,35,0)); }
          45%      { transform: scale(1.18); filter: drop-shadow(0 0 6px rgba(245,166,35,0.85)); }
        }
      `}</style>
    </section>
  );
}

function TestimonialCard({ r, idx, lang, isRTL }) {
  const name = lang === "en" ? r.name_en : r.name_ar;
  const role = lang === "en" ? r.role_en : r.role_ar;
  const text = lang === "en" ? r.text_en : r.text_ar;
  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, delay: idx * 0.1 }}
      dir={isRTL ? "rtl" : "ltr"}
      className="relative rounded-3xl bg-white p-6 md:p-7 flex flex-col text-start border border-neutral-200/80"
      style={{
        boxShadow:
          "0 30px 60px -30px rgba(15,23,42,0.20), 0 8px 20px -12px rgba(249,115,22,0.15)",
      }}
      data-testid={`testimonial-card-${r.id}`}
    >
      {/* Corner quote glyph */}
      <Quote
        className={`absolute top-4 ${isRTL ? "left-4" : "right-4"} w-8 h-8 text-brand-200`}
        strokeWidth={2}
      />

      {/* 5-star row with staggered twinkle */}
      <div className="flex items-center gap-1 mb-4">
        {Array.from({ length: r.stars }).map((_, i) => (
          <Star
            key={i}
            className="w-5 h-5 text-[#F5A623] fill-[#F5A623]"
            style={{
              animation: `tsTwinkle 2.4s ease-in-out ${i * 0.15}s infinite`,
            }}
          />
        ))}
      </div>

      {/* Full quote body — no truncation */}
      <p className="text-[14px] md:text-[15px] leading-[1.75] text-neutral-700 whitespace-pre-line flex-1 mb-5">
        {text}
      </p>

      {/* Author */}
      <div className="flex items-center gap-3 pt-4 border-t border-neutral-100">
        <div
          className={`w-11 h-11 rounded-full ${ACCENT_BG[r.accent] || ACCENT_BG.brand} flex items-center justify-center font-black text-lg shrink-0 shadow-md`}
        >
          {r.initials}
        </div>
        <div className="min-w-0">
          <div className="font-bold text-neutral-900 text-[15px] leading-tight">
            {name}
          </div>
          {role && (
            <div className="text-[12px] text-neutral-500 mt-0.5">{role}</div>
          )}
        </div>
      </div>
    </motion.article>
  );
}
